import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Stack,
  IconButton,
  Autocomplete,
  Paper,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { Close as CloseIcon, Add as AddIcon } from '@mui/icons-material'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { SmokingSession, useCreateSessionMutation, useUpdateSessionMutation } from '@store/api/sessionApi'
import { Cigar, useGetCigarsQuery } from '@store/api/cigarApi'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { offlineCreateSession, offlineUpdateSession } from '@services/offlineWriteService'
import MultiFactorRatingForm, { MultiFactorRatingValue, isCompleteRating } from '@components/sessions/MultiFactorRatingForm'
import {
  useGetSessionRatingQuery,
  useUpsertSessionRatingMutation,
  useGetRatingPreferencesQuery,
} from '@store/api/ratingApi'

const DEFAULT_MULTI_FACTOR_RATING: MultiFactorRatingValue = {
  flavor: 5,
  construction: 5,
  burn: 5,
  value: 5,
  isOverallManual: false,
}

interface SessionFormProps {
  open: boolean
  onClose: () => void
  session?: SmokingSession | null
  cigar?: Cigar | null
}

const qualityOptions = [
  { value: 'poor', label: 'Poor' },
  { value: 'fair', label: 'Fair' },
  { value: 'good', label: 'Good' },
  { value: 'excellent', label: 'Excellent' }
]

const ashColorOptions = [
  { value: 'white', label: 'White', color: '#f5f5f5' },
  { value: 'gray', label: 'Gray', color: '#9e9e9e' },
  { value: 'black', label: 'Black', color: '#424242' },
  { value: 'mixed', label: 'Mixed', color: '#ff9800' }
]

const settingOptions = [
  { value: 'indoor', label: 'Indoor' },
  { value: 'outdoor', label: 'Outdoor' },
  { value: 'patio', label: 'Patio' },
  { value: 'lounge', label: 'Lounge' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'club', label: 'Club' }
]

const weatherOptions = [
  { value: 'sunny', label: 'Sunny' },
  { value: 'cloudy', label: 'Cloudy' },
  { value: 'rainy', label: 'Rainy' },
  { value: 'windy', label: 'Windy' },
  { value: 'cold', label: 'Cold' },
  { value: 'hot', label: 'Hot' },
  { value: 'humid', label: 'Humid' }
]

const schema = yup.object({
  cigarId: yup.string().required('Please select a cigar'),
  smokedDate: yup.date().required('Date is required'),
  durationMinutes: yup.number().min(0, 'Duration must be positive').max(1440, 'Duration cannot exceed 24 hours'),
  tastingNotes: yup.string().max(500, 'Notes cannot exceed 500 characters'),
  pairing: yup.string().max(255, 'Pairing cannot exceed 255 characters'),
  location: yup.string().max(255, 'Location cannot exceed 255 characters'),
  weather: yup.string().max(100, 'Weather cannot exceed 100 characters'),
  temperature: yup.number().min(-60, 'Temperature too low').max(140, 'Temperature too high').nullable(),
  humidity: yup.number().min(0, 'Humidity must be positive').max(100, 'Humidity cannot exceed 100%').nullable(),
  setting: yup.string().max(100, 'Setting cannot exceed 100 characters'),
  preSmokingNotes: yup.string().max(500, 'Pre-smoking notes cannot exceed 500 characters'),
  drawQuality: yup.string().oneOf(['poor', 'fair', 'good', 'excellent']),
  burnQuality: yup.string().oneOf(['poor', 'fair', 'good', 'excellent']),
  ashColor: yup.string().oneOf(['white', 'gray', 'black', 'mixed']),
  imageUrls: yup.array().of(yup.string().url('Must be a valid URL'))
})

type FormData = yup.InferType<typeof schema>

function buildOfflineSession(
  cigar: Cigar,
  submitData: Record<string, unknown>,
  extras: { imageUrls: string[]; companions: string[]; isDraft?: boolean },
): SmokingSession {
  const now = new Date().toISOString()
  return {
    id: '',
    userId: '',
    cigarId: cigar.id,
    smokedDate: (submitData.smokedDate as string) ?? now,
    startTime: submitData.startTime as string | undefined,
    endTime: submitData.endTime as string | undefined,
    durationMinutes: submitData.durationMinutes as number | undefined,
    tastingNotes: submitData.tastingNotes as string | undefined,
    pairing: submitData.pairing as string | undefined,
    location: submitData.location as string | undefined,
    weather: submitData.weather as string | undefined,
    temperature: submitData.temperature as number | undefined,
    humidity: submitData.humidity as number | undefined,
    setting: submitData.setting as string | undefined,
    preSmokingNotes: submitData.preSmokingNotes as string | undefined,
    drawQuality: submitData.drawQuality as SmokingSession['drawQuality'],
    burnQuality: submitData.burnQuality as SmokingSession['burnQuality'],
    ashColor: submitData.ashColor as SmokingSession['ashColor'],
    imageUrls: extras.imageUrls,
    companions: extras.companions,
    isDraft: extras.isDraft ?? false,
    createdAt: now,
    updatedAt: now,
    cigar: {
      id: cigar.id,
      vitola: { id: cigar.vitola.id, name: cigar.vitola.name },
      cigarLine: {
        id: cigar.cigarLine.id,
        name: cigar.cigarLine.name,
        brand: { id: cigar.brand.id, name: cigar.brand.name },
      },
    },
  }
}

const SessionForm: React.FC<SessionFormProps> = ({
  open,
  onClose,
  session,
  cigar:   preselectedCigar
}) => {
  const dispatch = useAppDispatch()
  const { isOnline } = useAppSelector((state) => state.offline)
  const [imageUrl, setImageUrl] = useState('')
  const [imageUrls, setImageUrls] = useState<string[]>([])
  const [companions, setCompanions] = useState<string[]>([])
  const [companionInput, setCompanionInput] = useState('')
  const [cigarSearch, setCigarSearch] = useState('')
  const [selectedCigar, setSelectedCigar] = useState<Cigar | null>(null)
  const [includeRating, setIncludeRating] = useState(false)
  const [multiFactorRating, setMultiFactorRating] = useState<MultiFactorRatingValue>(DEFAULT_MULTI_FACTOR_RATING)

  const { data: cigarsData } = useGetCigarsQuery({ page: 1, limit: 100, search: cigarSearch || undefined, status: 'available' })
  const { data: existingRating } = useGetSessionRatingQuery(session?.id || '', { skip: !session?.id || !open })
  const { data: ratingPrefs } = useGetRatingPreferencesQuery(undefined, { skip: !open })
  const [createSession, { isLoading: createLoading }] = useCreateSessionMutation()
  const [updateSession, { isLoading: updateLoading }] = useUpdateSessionMutation()
  const [upsertSessionRating, { isLoading: ratingLoading }] = useUpsertSessionRatingMutation()
  const loading = createLoading || updateLoading || ratingLoading

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid }
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    mode: 'onTouched',
    defaultValues: {
      cigarId: '',
      smokedDate: new Date(),
      durationMinutes: undefined,
      tastingNotes: '',
      pairing: '',
      location: '',
      weather: '',
      temperature: undefined,
      humidity: undefined,
      setting: '',
      preSmokingNotes: '',
      drawQuality: '' as 'poor' | 'fair' | 'good' | 'excellent' | undefined,
      burnQuality: '' as 'poor' | 'fair' | 'good' | 'excellent' | undefined,
      ashColor: '' as 'white' | 'gray' | 'black' | 'mixed' | undefined,
      imageUrls: []
    }
  })

  useEffect(() => {
    if (existingRating) {
      setIncludeRating(true)
      setMultiFactorRating({
        flavor: existingRating.flavor,
        construction: existingRating.construction,
        burn: existingRating.burn,
        value: existingRating.value,
        overall: existingRating.overall,
        isOverallManual: existingRating.isOverallManual,
        flavorNotes: existingRating.flavorNotes || '',
        constructionNotes: existingRating.constructionNotes || '',
        burnNotes: existingRating.burnNotes || '',
        valueNotes: existingRating.valueNotes || '',
        overallNotes: existingRating.overallNotes || '',
      })
    } else if (!session) {
      setIncludeRating(false)
      setMultiFactorRating(DEFAULT_MULTI_FACTOR_RATING)
    }
  }, [existingRating, session])

  useEffect(() => {
    if (session) {
      reset({
        cigarId: session.cigarId,
        smokedDate: new Date(session.smokedDate),
        durationMinutes: session.durationMinutes || undefined,
        tastingNotes: session.tastingNotes || '',
        pairing: session.pairing || '',
        location: session.location || '',
        weather: session.weather || '',
        temperature: session.temperature ?? undefined,
        humidity: session.humidity ?? undefined,
        setting: session.setting || '',
        preSmokingNotes: session.preSmokingNotes || '',
        drawQuality: (session.drawQuality || '') as 'poor' | 'fair' | 'good' | 'excellent' | undefined,
        burnQuality: (session.burnQuality || '') as 'poor' | 'fair' | 'good' | 'excellent' | undefined,
        ashColor: (session.ashColor || '') as 'white' | 'gray' | 'black' | 'mixed' | undefined,
        imageUrls: session.imageUrls || []
      })
      setImageUrls(session.imageUrls || [])
      setCompanions(session.companions || [])
      // Set selected cigar for edit mode preview
      const matchedCigar = cigarsData?.cigars?.find((c: Cigar) => c.id === session.cigarId)
      if (matchedCigar) setSelectedCigar(matchedCigar)
    } else {
      reset({
        cigarId: preselectedCigar?.id || '',
        smokedDate: new Date(),
        durationMinutes: undefined,
        tastingNotes: '',
        pairing: '',
        location: '',
        weather: '',
        temperature: undefined,
        humidity: undefined,
        setting: '',
        preSmokingNotes: '',
        drawQuality: '' as 'poor' | 'fair' | 'good' | 'excellent' | undefined,
        burnQuality: '' as 'poor' | 'fair' | 'good' | 'excellent' | undefined,
        ashColor: '' as 'white' | 'gray' | 'black' | 'mixed' | undefined,
        imageUrls: []
      })
      setImageUrls([])
      setCompanions([])
      setSelectedCigar(preselectedCigar || null)
      setCigarSearch('')
    }
  }, [session, reset, cigarsData, preselectedCigar])

  const handleAddImage = () => {
    if (imageUrl.trim() && !imageUrls.includes(imageUrl.trim())) {
      const newImageUrls = [...imageUrls, imageUrl.trim()]
      setImageUrls(newImageUrls)
      setValue('imageUrls', newImageUrls)
      setImageUrl('')
    }
  }

  const handleRemoveImage = (index: number) => {
    const newImageUrls = imageUrls.filter((_, i) => i !== index)
    setImageUrls(newImageUrls)
    setValue('imageUrls', newImageUrls)
  }

  const handleAddCompanion = () => {
    if (companionInput.trim() && !companions.includes(companionInput.trim())) {
      setCompanions([...companions, companionInput.trim()])
      setCompanionInput('')
    }
  }

  const handleRemoveCompanion = (index: number) => {
    setCompanions(companions.filter((_, i) => i !== index))
  }

  // "Record Past Session" — records a completed session immediately
  const handleFormSubmit = async (data: FormData) => {
    try {
      const submitData = {
        ...data,
        smokedDate: data.smokedDate.toISOString(),
        imageUrls: imageUrls,
        companions: companions,
        temperature: data.temperature ?? undefined,
        humidity: data.humidity ?? undefined,
        drawQuality: (data.drawQuality || undefined) as 'poor' | 'fair' | 'good' | 'excellent' | undefined,
        burnQuality: (data.burnQuality || undefined) as 'poor' | 'fair' | 'good' | 'excellent' | undefined,
        ashColor: (data.ashColor || undefined) as 'white' | 'gray' | 'black' | 'mixed' | undefined,
      }

      let savedSession: SmokingSession
      if (!isOnline) {
        const cigar =
          selectedCigar ??
          cigarsData?.cigars?.find((c: Cigar) => c.id === data.cigarId) ??
          (session?.cigar
            ? ({
                id: session.cigarId,
                vitola: session.cigar.vitola,
                cigarLine: session.cigar.cigarLine,
                brand: session.cigar.cigarLine.brand,
              } as unknown as Cigar)
            : null)
        if (!cigar) {
          console.error('Cannot save session offline without cigar context')
          return
        }
        if (session) {
          const merged = buildOfflineSession(cigar, submitData, { imageUrls, companions })
          const record: SmokingSession = { ...session, ...merged, id: session.id }
          await offlineUpdateSession(dispatch, session.id, submitData, record)
        } else {
          await offlineCreateSession(
            dispatch,
            submitData,
            buildOfflineSession(cigar, submitData, { imageUrls, companions }),
          )
        }
        handleClose()
        return
      }

      if (session) {
        savedSession = await updateSession({ id: session.id, updates: submitData }).unwrap()
      } else {
        savedSession = await createSession(submitData).unwrap()
      }

      if (includeRating && isCompleteRating(multiFactorRating)) {
        await upsertSessionRating({
          sessionId: savedSession.id,
          rating: multiFactorRating,
        }).unwrap()
      }

      handleClose()
    } catch (error) {
      console.error('Failed to save session:', error)
    }
  }

  // "Start Session" — creates an active session with timer, uses a cigar
  const handleStartSession = async () => {
    const data = watch() as FormData
    if (!data.cigarId) return
    const cigar = selectedCigar ?? cigarsData?.cigars?.find((c: Cigar) => c.id === data.cigarId)
    if (!cigar) return
    try {
      const payload = {
        cigarId: data.cigarId,
        smokedDate: new Date().toISOString(),
        startTime: new Date().toISOString(),
        isDraft: true,
      }
      if (!isOnline) {
        await offlineCreateSession(
          dispatch,
          payload,
          buildOfflineSession(cigar, payload, { imageUrls: [], companions: [], isDraft: true }),
        )
        handleClose()
        return
      }
      await createSession(payload).unwrap()
      handleClose()
    } catch (error) {
      console.error('Failed to start session:', error)
    }
  }

  const handleClose = () => {
    reset()
    setImageUrls([])
    setImageUrl('')
    setCompanions([])
    setCompanionInput('')
    setSelectedCigar(null)
    setCigarSearch('')
    setIncludeRating(false)
    setMultiFactorRating(DEFAULT_MULTI_FACTOR_RATING)
    onClose()
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {session ? 'Edit Session' : 'Smoke a Cigar'}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <DialogContent>
            <Grid container spacing={3}>
              {/* Cigar Selection with Search */}
              <Grid item xs={12}>
                <Controller
                  name="cigarId"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={cigarsData?.cigars || []}
                      getOptionLabel={(cigar: Cigar) => `${cigar.brand.name} ${cigar.cigarLine.name} - ${cigar.vitola.name}`}
                      value={selectedCigar}
                      onChange={(_event: React.SyntheticEvent, newValue: Cigar | null) => {
                        setSelectedCigar(newValue)
                        field.onChange(newValue?.id || '')
                      }}
                      onInputChange={(_event: React.SyntheticEvent, newInputValue: string) => {
                        setCigarSearch(newInputValue)
                      }}
                      isOptionEqualToValue={(option: Cigar, value: Cigar) => option.id === value.id}
                      disabled={loading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search Cigars"
                          placeholder="Type to search by brand, line, or vitola..."
                          error={!!errors.cigarId}
                          helperText={errors.cigarId?.message}
                        />
                      )}
                      renderOption={(props, cigar: Cigar) => (
                        <li {...props} key={cigar.id}>
                          <Box>
                            <Typography variant="body1">
                              {cigar.brand.name} {cigar.cigarLine.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {cigar.vitola.name} • {cigar.humidor.name} • Qty: {cigar.quantity}
                            </Typography>
                          </Box>
                        </li>
                      )}
                      noOptionsText="No cigars found in your collection"
                      fullWidth
                    />
                  )}
                />
              </Grid>

              {/* Cigar Details Preview */}
              {selectedCigar && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Selected Cigar Details
                    </Typography>
                    <Stack direction="row" spacing={2} flexWrap="wrap">
                      <Typography variant="body2"><strong>Brand:</strong> {selectedCigar.brand.name}</Typography>
                      <Typography variant="body2"><strong>Line:</strong> {selectedCigar.cigarLine.name}</Typography>
                      <Typography variant="body2"><strong>Vitola:</strong> {selectedCigar.vitola.name}</Typography>
                      {selectedCigar.wrapperType && (
                        <Typography variant="body2"><strong>Wrapper:</strong> {selectedCigar.wrapperType}</Typography>
                      )}
                      {selectedCigar.strengthLevel && (
                        <Typography variant="body2"><strong>Strength:</strong> {selectedCigar.strengthLevel}</Typography>
                      )}
                      <Typography variant="body2"><strong>Humidor:</strong> {selectedCigar.humidor.name}</Typography>
                    </Stack>
                  </Paper>
                </Grid>
              )}

              {/* Date and Duration */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="smokedDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      label="Smoked Date"
                      disabled={loading}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.smokedDate,
                          helperText: errors.smokedDate?.message
                        }
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="durationMinutes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Duration (minutes)"
                      type="number"
                      fullWidth
                      disabled={loading}
                      error={!!errors.durationMinutes}
                      helperText={errors.durationMinutes?.message}
                    />
                  )}
                />
              </Grid>

              {/* Multi-Factor Rating */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeRating}
                      onChange={(e) => setIncludeRating(e.target.checked)}
                      disabled={loading}
                    />
                  }
                  label="Rate this session (5-factor, 1–10 scale)"
                />
                {includeRating && (
                  <MultiFactorRatingForm
                    value={multiFactorRating}
                    onChange={setMultiFactorRating}
                    disabled={loading}
                    weights={ratingPrefs?.weights}
                  />
                )}
              </Grid>

              {/* Quality Indicators */}
              <Grid item xs={12} sm={4}>
                <Controller
                  name="drawQuality"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Draw Quality</InputLabel>
                      <Select
                        {...field}
                        label="Draw Quality"
                        disabled={loading}
                      >
                        {qualityOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="burnQuality"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Burn Quality</InputLabel>
                      <Select
                        {...field}
                        label="Burn Quality"
                        disabled={loading}
                      >
                        {qualityOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="ashColor"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Ash Color</InputLabel>
                      <Select
                        {...field}
                        label="Ash Color"
                        disabled={loading}
                      >
                        {ashColorOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Box
                                width={16}
                                height={16}
                                borderRadius="50%"
                                bgcolor={option.color}
                                border="1px solid #ccc"
                              />
                              {option.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Pre-Smoking Notes */}
              <Grid item xs={12}>
                <Controller
                  name="preSmokingNotes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Pre-Smoking Observations & Expectations"
                      multiline
                      rows={2}
                      fullWidth
                      disabled={loading}
                      error={!!errors.preSmokingNotes}
                      helperText={errors.preSmokingNotes?.message}
                    />
                  )}
                />
              </Grid>

              {/* Location and Weather */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="location"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Location"
                      fullWidth
                      disabled={loading}
                      error={!!errors.location}
                      helperText={errors.location?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="weather"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Weather</InputLabel>
                      <Select
                        {...field}
                        label="Weather"
                        disabled={loading}
                      >
                        {weatherOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Environment Conditions */}
              <Grid item xs={12} sm={4}>
                <Controller
                  name="setting"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Setting</InputLabel>
                      <Select
                        {...field}
                        label="Setting"
                        disabled={loading}
                      >
                        {settingOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="temperature"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Temperature (°F)"
                      type="number"
                      fullWidth
                      disabled={loading}
                      error={!!errors.temperature}
                      helperText={errors.temperature?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <Controller
                  name="humidity"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Humidity (%)"
                      type="number"
                      fullWidth
                      disabled={loading}
                      error={!!errors.humidity}
                      helperText={errors.humidity?.message}
                    />
                  )}
                />
              </Grid>

              {/* Companions */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Companions
                </Typography>
                <Box display="flex" gap={1} mb={2}>
                  <TextField
                    value={companionInput}
                    onChange={(e) => setCompanionInput(e.target.value)}
                    placeholder="Add companion name"
                    fullWidth
                    disabled={loading}
                    size="small"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleAddCompanion()
                      }
                    }}
                  />
                  <Button
                    onClick={handleAddCompanion}
                    disabled={!companionInput.trim() || loading}
                    variant="outlined"
                    startIcon={<AddIcon />}
                  >
                    Add
                  </Button>
                </Box>
                {companions.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {companions.map((name, index) => (
                      <Chip
                        key={index}
                        label={name}
                        onDelete={() => handleRemoveCompanion(index)}
                        disabled={loading}
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                )}
              </Grid>

              {/* Pairing */}
              <Grid item xs={12}>
                <Controller
                  name="pairing"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Pairing (drink, food, etc.)"
                      fullWidth
                      disabled={loading}
                      error={!!errors.pairing}
                      helperText={errors.pairing?.message}
                    />
                  )}
                />
              </Grid>

              {/* Tasting Notes */}
              <Grid item xs={12}>
                <Controller
                  name="tastingNotes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Tasting Notes"
                      multiline
                      rows={4}
                      fullWidth
                      disabled={loading}
                      error={!!errors.tastingNotes}
                      helperText={errors.tastingNotes?.message}
                    />
                  )}
                />
              </Grid>

              {/* Image URLs */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Session Photos
                </Typography>
                <Box display="flex" gap={1} mb={2}>
                  <TextField
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="Add image URL"
                    fullWidth
                    disabled={loading}
                    size="small"
                  />
                  <Button
                    onClick={handleAddImage}
                    disabled={!imageUrl.trim() || loading}
                    variant="outlined"
                    startIcon={<AddIcon />}
                  >
                    Add
                  </Button>
                </Box>
                {imageUrls.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {imageUrls.map((_, index) => (
                      <Chip
                        key={index}
                        label={`Image ${index + 1}`}
                        onDelete={() => handleRemoveImage(index)}
                        disabled={loading}
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                )}
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            {!session && (
              <Button
                onClick={handleStartSession}
                disabled={loading || !watch('cigarId')}
                variant="contained"
                color="success"
              >
                {loading ? 'Starting...' : 'Start Session'}
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              disabled={!isValid || loading}
            >
              {loading ? 'Saving...' : session ? 'Update Session' : 'Record Past Session'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  )
}

export default SessionForm
